import {
  Center,
  Flex,
  HStack,
  IconButton,
  Select,
  Text,
  Pagination as _Pagination,
  createListCollection,
} from '@chakra-ui/react';
import { UseNavigateResult } from '@tanstack/react-router';
import { t } from 'i18next';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';

const perPages = createListCollection({
  items: [
    { label: '10', value: '10' },
    { label: '25', value: '25' },
    { label: '50', value: '50' },
    { label: '100', value: '100' },
    { label: t('ui.all'), value: '9999' },
  ],
});

export const Pagination = ({
  isPaginated,
  count = 0,
  page = 1,
  pageSize,
  navigate,
}: {
  isPaginated: boolean;
  count: number;
  page: number;
  pageSize: number;
  navigate: UseNavigateResult<''>;
}) => {
  const totalPages = Math.ceil(count / pageSize);

  const getVisiblePages = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);

    const pages: (number | 'ellipsis')[] = [];
    const left = Math.max(2, page - 1);
    const right = Math.min(totalPages - 1, page + 1);

    pages.push(1);

    if (left > 2) pages.push('ellipsis');

    for (let i = left; i <= right; i++) {
      pages.push(i);
    }

    if (right < totalPages - 1) pages.push('ellipsis');

    pages.push(totalPages);

    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <Flex alignItems="center" justifyContent="space-between" w="full" gap={4} padding={2}>
      <HStack>
        <Text fontFamily={'Inter'} fontSize={'0.9rem'}>
          Total{' '}
        </Text>{' '}
        <Text fontWeight={'bold'}>{count}</Text>
      </HStack>

      <Flex alignItems="center" gap={4} flexShrink={0}>
        {isPaginated && (
          <_Pagination.Root
            count={count}
            pageSize={pageSize}
            page={page}
            onPageChange={(pageDetails) =>
              navigate({
                search: (prev: Record<string, any>) => ({
                  ...prev,
                  page: pageDetails.page,
                }),
              })
            }
          >
            <Center gap={2}>
              <_Pagination.PrevTrigger asChild>
                <IconButton variant="ghost" size="sm">
                  <HiChevronLeft />
                </IconButton>
              </_Pagination.PrevTrigger>

              {visiblePages.map((p, idx) =>
                p === 'ellipsis' ? (
                  <Text key={`ellipsis-${idx}`} mx={1}>
                    ...
                  </Text>
                ) : (
                  <_Pagination.Item key={p} type="page" value={p} asChild>
                    <IconButton
                      variant="ghost"
                      w={8}
                      h={8}
                      minW={8}
                      minH={8}
                      fontSize="sm"
                      bg={p === page ? '#269584' : 'white'}
                      color={p === page ? 'white' : '##8D8D8D'}
                    >
                      {p}
                    </IconButton>
                  </_Pagination.Item>
                )
              )}

              <_Pagination.NextTrigger asChild>
                <IconButton variant="ghost" size="sm">
                  <HiChevronRight />
                </IconButton>
              </_Pagination.NextTrigger>
            </Center>
          </_Pagination.Root>
        )}
      </Flex>

      <Flex alignItems={'center'} columnGap={1}>
        <Text color={'#9095AA'} fontSize={'0.8rem'} fontFamily={'Inter'}>
          Afficher par page
        </Text>
        <Select.Root
          size="sm"
          collection={perPages}
          bg={'white'}
          width="76px"
          value={[pageSize.toString()]}
          onValueChange={(e) =>
            navigate({
              search: (prev: Record<string, any>) => ({
                ...prev,
                pageSize: Number.parseInt(e.value[0]),
              }),
            })
          }
        >
          <Select.HiddenSelect />
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText placeholder="Per page" />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              {perPages.items.map((item) => (
                <Select.Item key={item.value} item={item}>
                  {item.label}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      </Flex>
    </Flex>
  );
};
